import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Alert, Badge, Button, Card, Col, Container, Image, Row, Tab, Tabs, ToggleButton, ToggleButtonGroup} from "react-bootstrap";
import {observer} from "mobx-react-lite";
import {FaBookOpen, FaCalendar, FaCheckCircle, FaClock, FaGlobe, FaSearch} from "react-icons/fa";
import {Context} from "../../index";
import BookList from "../bookspages/BookList";
import AssignUserList from "../assignmentpages/AssignUserList";
import AssignUnderBookS1 from "../bookspages/AssignUnderBookS1";
import AssignUnderBookS2 from "../bookspages/AssignUnderBookS2";
import AssignUnderBookS3 from "../bookspages/AssignUnderBookS3";
import AssignUnderBookS4 from "../bookspages/AssignUnderBookS4";
import {fetchOwnerAssignmentsByBook} from "../../http/bookAPI";
import Loader from "../Loader";

const SEARCHING_STATUSES = ["NEW", "SUSPENDED"];
const WORKING_STATUSES = ["READING", "READING_RIDT", "READING_RE", "REPORTED", "REPORTED_RFU", "REPORTED_RV", "REPORTED_EXP"];
const COMPLETE_STATUSES = ["SUCCESS"];

const statusModeConfig = {
    searching: {
        label: "Searching",
        icon: <FaSearch className="me-1" />,
        statuses: SEARCHING_STATUSES,
        empty: "You do not have assignments currently searching for participants.",
    },
    working: {
        label: "Working on",
        icon: <FaClock className="me-1" />,
        statuses: WORKING_STATUSES,
        empty: "You do not have assignments currently in review work.",
    },
    complete: {
        label: "Complete",
        icon: <FaCheckCircle className="me-1" />,
        statuses: COMPLETE_STATUSES,
        empty: "You do not have completed assignments yet.",
    },
};

const getAssignmentBook = (assignment) => assignment?.book || assignment?.Book || null;

const isBookOnTopActive = (bookData) => {
    const untilMs = Number(bookData?.always_on_top_until || 0);
    return Number.isFinite(untilMs) && untilMs > Date.now();
};

const isAssignmentOnTopActive = (assignment) => {
    const untilMs = Number(assignment?.always_on_top_until || 0);
    return Number.isFinite(untilMs) && untilMs > Date.now();
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).replace(/ /g, "-");
};

const getCoverSrc = (bookData) => (
    bookData?.cover_file
        ? `${process.env.REACT_APP_API_URL}covers/${bookData.cover_file}`
        : `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`
);

const buildBookGroups = (assignments) => {
    const groupsMap = new Map();

    assignments.forEach((assignment) => {
        const bookData = getAssignmentBook(assignment);
        const bookId = assignment?.bookId || bookData?.id;

        if (!bookId || !bookData) return;

        if (!groupsMap.has(bookId)) {
            groupsMap.set(bookId, {
                book: bookData,
                assignments: [],
            });
        }

        groupsMap.get(bookId).assignments.push(assignment);
    });

    return Array.from(groupsMap.values())
        .map((group) => ({
            ...group,
            latestAssignmentId: Math.max(...group.assignments.map((assignment) => Number(assignment?.id || 0))),
        }))
        .sort((a, b) => b.latestAssignmentId - a.latestAssignmentId);
};

const AssignmentTile = ({assignment, mode, groupAssignments, reloadAssignments}) => {
    const bookData = getAssignmentBook(assignment);
    const bookOnTop = isBookOnTopActive(bookData);
    const activeAssignmentOnTop = groupAssignments.find((item) => isAssignmentOnTopActive(item));
    const itemLabel = bookData?.book_type === "product" ? "Product" : "Book";

    if (mode === "searching") {
        return (
            <AssignUnderBookS1
                assign={assignment}
                isBookOnTopActive={bookOnTop}
                bookOnTopLabel={`${itemLabel} on top`}
                activeAssignmentOnTopId={activeAssignmentOnTop?.id || null}
                updateAssignments={reloadAssignments}
            />
        );
    }

    if (["READING", "READING_RIDT", "READING_RE"].includes(assignment.assignment_status)) {
        return <AssignUnderBookS2 assign={assignment} />;
    }

    if (["REPORTED", "REPORTED_RFU", "REPORTED_RV", "REPORTED_EXP"].includes(assignment.assignment_status)) {
        return <AssignUnderBookS3 assign={assignment} />;
    }

    return <AssignUnderBookS4 assign={assignment} updateAssignments={reloadAssignments} />;
};

const OwnerAssignmentsGrouped = observer(({onBookSelect, setActiveComponent}) => {
    const {book: bookStore} = useContext(Context);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState(localStorage.getItem("ownerAssignmentsBookMode") || "searching");
    const [renderMode, setRenderMode] = useState(localStorage.getItem("ownerAssignmentsBookMode") || "searching");
    const [modeLoading, setModeLoading] = useState(false);

    const loadAssignments = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchOwnerAssignmentsByBook();
            setAssignments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load owner assignments:", error);
            setAssignments([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAssignments();
    }, [loadAssignments]);

    const filteredGroups = useMemo(() => {
        const statuses = statusModeConfig[renderMode]?.statuses || SEARCHING_STATUSES;
        return buildBookGroups(assignments)
            .map((group) => ({
                ...group,
                visibleAssignments: group.assignments.filter((assignment) => statuses.includes(assignment?.assignment_status)),
            }))
            .filter((group) => group.visibleAssignments.length > 0);
    }, [assignments, renderMode]);

    const handleModeChange = (value) => {
        if (!value) return;
        setMode(value);
        localStorage.setItem("ownerAssignmentsBookMode", value);
        setModeLoading(true);
        window.setTimeout(() => {
            setRenderMode(value);
            setModeLoading(false);
        }, 80);
    };

    const openBook = (bookData) => {
        if (!bookData) return;
        bookStore.setSelectedCurrentUserBook(bookData);
        if (typeof onBookSelect === "function") {
            onBookSelect(bookData);
            return;
        }
        if (typeof setActiveComponent === "function") {
            setActiveComponent("BookFull");
        }
    };

    return (
        <>
            <div className="card justify-content-start text-start start-0 mt-2 mb-2 p-2 small bg-white rounded">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h6 className="text-success mb-0 ms-1">Assignments for Your Books & Products</h6>
                    <ToggleButtonGroup
                        type="radio"
                        name="ownerAssignmentsBookMode"
                        value={mode}
                        onChange={handleModeChange}
                        size="sm"
                    >
                        {Object.entries(statusModeConfig).map(([key, config]) => (
                            <ToggleButton key={key} id={`owner-assignments-${key}`} variant="outline-success" value={key}>
                                {config.icon}
                                {config.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </div>
            </div>

            {loading || modeLoading ? (
                <div className="mt-3">
                    <Loader />
                </div>
            ) : filteredGroups.length === 0 ? (
                <Alert variant="light" className="text-muted text-center mt-2">
                    {statusModeConfig[mode]?.empty}
                </Alert>
            ) : (
                filteredGroups.map(({book: bookData, assignments: groupAssignments, visibleAssignments}) => {
                    const searchingCount = groupAssignments.filter((assignment) => SEARCHING_STATUSES.includes(assignment.assignment_status)).length;
                    const workingCount = groupAssignments.filter((assignment) => WORKING_STATUSES.includes(assignment.assignment_status)).length;
                    const completeCount = groupAssignments.filter((assignment) => COMPLETE_STATUSES.includes(assignment.assignment_status)).length;
                    const itemLabel = bookData?.book_type === "product" ? "Product" : "Book";

                    return (
                        <Card key={bookData.id} className="justify-content-start text-start start-0 mt-2 mb-2 p-2 small bg-white rounded">
                            <div className="d-flex gap-2 align-items-center mb-2">
                                <Image
                                    src={getCoverSrc(bookData)}
                                    onError={(event) => {
                                        event.currentTarget.src = `${process.env.REACT_APP_API_URL}covers/empty-cover.jpg`;
                                    }}
                                    style={{
                                        width: "48px",
                                        height: "64px",
                                        objectFit: "contain",
                                        borderRadius: "4px",
                                        backgroundColor: "white",
                                    }}
                                />
                                <div className="flex-grow-1" style={{minWidth: 0}}>
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <strong className="text-muted text-truncate" style={{maxWidth: "520px"}}>
                                            {bookData.title}
                                        </strong>
                                        <Badge bg="success">{itemLabel}</Badge>
                                        <span className="text-muted">#{bookData.id}</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 text-muted">
                                        <span><FaBookOpen className="me-1" />A/S: <strong>{bookData.author || "-"}</strong></span>
                                        <span><FaCalendar className="me-1" />Added: <strong>{formatDate(bookData.createdAt)}</strong></span>
                                        <span><FaGlobe className="me-1" />Lang: <strong>{bookData.language || bookData.lang || "-"}</strong></span>
                                        <span>Searching: <strong>{searchingCount}</strong></span>
                                        <span>Working: <strong>{workingCount}</strong></span>
                                        <span>Complete: <strong>{completeCount}</strong></span>
                                    </div>
                                </div>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => openBook(bookData)}
                                >
                                    Open
                                </Button>
                            </div>

                            <Container fluid className="p-0">
                                <Row className="gx-2 gy-1">
                                    {visibleAssignments.map((assignment) => (
                                        <Col key={assignment.id} md={mode === "searching" ? 3 : 4} className="px-1 mx-auto mx-md-0">
                                            <AssignmentTile
                                                assignment={assignment}
                                                mode={mode}
                                                groupAssignments={groupAssignments}
                                                reloadAssignments={loadAssignments}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </Container>
                        </Card>
                    );
                })
            )}
        </>
    );
});

const AssignmentsBooksInDashboard = observer(({onBookSelect, onAssignSelect, setActiveComponent}) => {
    const [activeTab, setActiveTab] = useState(localStorage.getItem("assignmentsBooksDashboardTab") || "owner");

    const handleTabSelect = (key) => {
        if (!key) return;
        setActiveTab(key);
        localStorage.setItem("assignmentsBooksDashboardTab", key);
    };

    return (
        <Tabs
            activeKey={activeTab}
            onSelect={handleTabSelect}
            className="mt-3"
            justify
        >
            <Tab eventKey="owner" title="Assignments for Your Books & Products">
                <OwnerAssignmentsGrouped onBookSelect={onBookSelect} setActiveComponent={setActiveComponent} />
            </Tab>
            <Tab eventKey="books" title="Books & Products">
                <BookList onBookSelect={onBookSelect} setActiveComponent={setActiveComponent} />
            </Tab>
            <Tab eventKey="assignments" title="Assignments">
                <AssignUserList onAssignSelect={onAssignSelect} setActiveComponent={setActiveComponent} />
            </Tab>
        </Tabs>
    );
});

export default AssignmentsBooksInDashboard;
